//batchlist
import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import Select from "react-select";
import * as XLSX from "xlsx";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { Link, useLocation } from "react-router-dom";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

const styles = {
    pageTitleBar: {
        marginBottom: "20px"
    },

    pageTitle: {
        fontSize: "28px",
        fontWeight: "600",
        color: "#0f172a",
        marginBottom: "4px"
    },

    pageSubtitle: {
        color: "#64748b",
        fontSize: "14px"
    },

    filterCard: {
        background: "#fff",
        borderRadius: "8px",
        padding: "15px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        marginBottom: "15px"
    },

    compactLabel: {
        fontSize: "12px",
        fontWeight: "600",
        color: "#334155",
        marginBottom: "4px",
        display: "block"
    },

    dateInput: {
        width: "100%",
        height: "38px",
        padding: "6px 10px",
        fontSize: "13px",
        color: "#0f172a",
        border: "1px solid #cbd5e1",
        borderRadius: "6px",
        boxSizing: "border-box"
    },

    exportBtn: {
        background: "#16a34a",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer"
    },

    exportBtnDisabled: {
        background: "#94a3b8",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "not-allowed"
    },

    tableContainer: {
        background: "#fff",
        borderRadius: "12px",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        padding: "10px",
        border: "1px solid #e2e8f0",
        borderBottom: "none",
        overflowX: "auto"
    },

    erpTable: {
        marginBottom: 0,
        fontSize: "13px"
    },

    erpTh: {
        background: "#ffffff",
        color: "#0f172a",
        fontWeight: "600",
        padding: "12px",
        borderBottom: "1px solid #e2e8f0",
        whiteSpace: "nowrap"
    },

    erpTd: {
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        whiteSpace: "nowrap",
        color: "#334155"
    },

    cellLink: {
        color: "#2563eb",
        textDecoration: "none",
        fontWeight: 600,
        cursor: "pointer"
    },

    paginationBar: {
        background: "#fff",
        borderRadius: "12px",
        padding: "14px 20px",
        border: "1px solid #e2e8f0",
        borderTop: "none",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
    },

    paginationInfo: {
        fontSize: "13px",
        color: "#334155"
    },

    paginationButtons: {
        display: "flex",
        gap: "8px"
    },

    paginationBtn: {
        padding: "6px 16px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "6px",
        border: "1px solid #e2e8f0",
        background: "#fff",
        color: "#334155",
        cursor: "pointer"
    },

    paginationBtnDisabled: {
        padding: "6px 16px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "6px",
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        color: "#cbd5e1",
        cursor: "not-allowed"
    },

    pageSizeSelect: {
        padding: "6px 10px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "6px",
        border: "1px solid #e2e8f0",
        background: "#fff",
        color: "#334155",
        cursor: "pointer",
        outline: "none"
    },

    tabBarWrap: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "15px"
    },

    tabBar: {
        display: "inline-flex",
        gap: "2px",
        background: "#f1f5f9",
        borderRadius: "10px",
        padding: "4px"
    },

    tabBtn: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "7px 14px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "8px",
        border: "none",
        background: "transparent",
        color: "#64748b",
        cursor: "pointer"
    },

    tabBtnActive: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "7px 14px",
        fontSize: "13px",
        fontWeight: "600",
        borderRadius: "8px",
        border: "none",
        background: "#fff",
        color: "#0f172a",
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        cursor: "pointer"
    },

    tabBadge: {
        fontSize: "11px",
        fontWeight: "700",
        color: "#2563eb",
        background: "#dbeafe",
        borderRadius: "999px",
        padding: "1px 7px",
        lineHeight: "16px"
    },

    tabBadgeInactive: {
        fontSize: "11px",
        fontWeight: "700",
        color: "#64748b",
        background: "#e2e8f0",
        borderRadius: "999px",
        padding: "1px 7px",
        lineHeight: "16px"
    }
};

// Strips unnecessary trailing decimal zeros from a qty value coming back
// from the DB as a fixed-precision string (e.g. "100.00" -> "100",
// "25.50" -> "25.5"). Leaves non-numeric / empty values as-is.
const formatQty = (qty) => {
    if (qty === null || qty === undefined || qty === "") return qty;
    const num = Number(qty);
    if (Number.isNaN(num)) return qty;
    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

// Renders a datetime (e.g. "2026-09-02T10:15:00.000Z") as "02-09-2026" for
// display (dd-mm-yyyy, numeric month). Leaves null/invalid values as "-".
const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 30, 40, 50];

// ── Type-specific attribute filters (mirrors Product.js) ──
// Raw Material  -> Category, Sub Category, Quality Category
// Anything else (or no type selected) -> cable-style attribute filters
const FINISHED_FILTERS = [
    { key: "category", label: "Category" },
    { key: "cableType", label: "Cable Type" },
    { key: "code", label: "Code" },
    { key: "color", label: "Color" },
    { key: "noOfCores", label: "Cores" },
    { key: "nominalCrossSection", label: "Cross Section" },
    { key: "referenceOfStandard", label: "Std. Ref" },
    { key: "voltage", label: "Voltage" },
    { key: "construction", label: "Construction" },
];

const RAW_MATERIAL_FILTERS = [
    { key: "category", label: "Category" },
    { key: "subCategory", label: "Sub Category" },
    { key: "qualityCategory", label: "Quality Category" },
];

// ── Styles for the cable-attribute filter row (matches Production Order) ──
const cableFilterSelectStyles = {
    control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
};

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "34px",
        height: "34px",
        fontSize: "12px"
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "34px",
        padding: "0 8px"
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "34px"
    }),
    option: (provided) => ({
        ...provided,
        fontSize: "12px"
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: "12px"
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: "12px"
    })
};

const USER_MANUAL_SCOPE = {
    moduleName: "Logistics",
    process: "Inventory Management",
    subProcess: "Stock Report"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

const BatchList = () => {
    const location = useLocation();
    const [tableData, setTableData] = useState([]);

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // ── "Product" / "Inspected" / "Rejected" view toggle ──
    const [activeTab, setActiveTab] = useState("inspected");

    const [currentPage, setCurrentPage] = useState(1);
    const [dateDisplayText, setDateDisplayText] = useState("");
    const [pageSize, setPageSize] = useState(10);

    const [productTypes, setProductTypes] = useState([]);
    // Raw product-code -> label ("CODE - Description") lookup, restricted to codes
    // that actually have stock (from batch-list/product-codes). Used only for
    // labeling; the actual cascading options come from `products` below.
    const [allProductCodeOptions, setAllProductCodeOptions] = useState([]);
    const [batchNumberOptions, setBatchNumberOptions] = useState([]);
    const [storageLocationOptions, setStorageLocationOptions] = useState([]);

    // ── Attribute filter options come from the same API Product.js uses
    // (GET /product-master/filter-options), already cascaded by the backend. ──
    const [filterOptions, setFilterOptions] = useState({
        category: [], cableType: [], code: [], color: [], noOfCores: [],
        nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [],
        subCategory: [], qualityCategory: []
    });

    // Product code / description list for the selected Product Type (same API as Product.js)
    const [codeDescList, setCodeDescList] = useState([]);

    const [filters, setFilters] = useState({
        productType: null,
        productCode: null,
        batchNumber: null,
        storageLocation: null,
        construction: null,
        code: null,
        cableType: null,
        category: null,
        subCategory: null,
        qualityCategory: null,
        noOfCores: null,
        color: null,
        nominalCrossSection: null,
        referenceOfStandard: null,
        voltage: null,
        // Plain "YYYY-MM-DD" string (not a react-select option) — filters stock
        // rows by their created_at (the date the stock row was created).
        date: ""
    });

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        fetchStockReport();
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const loadFilterOptions = async () => {
        try {

            const [
                productTypesRes,
                productCodesRes,
                batchNumbersRes,
                storageLocationsRes
            ] = await Promise.all([
                axios.get(`${MASTER_API}/configuration/product-types`),
                axios.get(`${PURCHASE_API}/batch-list/product-codes`),
                axios.get(`${PURCHASE_API}/batch-list/batch-numbers`),
                axios.get(`${PURCHASE_API}/batch-list/storage-locations`)
            ]);

            // ── Product Type options come from the configuration master list ──
            // ── (Configuration → Master Data → Product Type) rather than distinct ──
            // ── values off batch records, so every configured type shows up and ──
            // ── inconsistent casing/duplicates from raw data are avoided. ──
            setProductTypes(
                (productTypesRes.data || []).map((r) => {
                    const val = String(r.name || r.productType || "").trim();
                    return { value: val, label: val };
                }).filter((o) => o.value !== "")
            );

            setAllProductCodeOptions(
                (productCodesRes.data.data || []).map((r) => ({
                    value: r.productCode,
                    label: r.productDescription
                        ? `${r.productCode} - ${r.productDescription}`
                        : r.productCode
                }))
            );

            setBatchNumberOptions(
                (batchNumbersRes.data.data || []).map((r) => ({
                    value: r.batch_number,
                    label: r.batch_number
                }))
            );

            setStorageLocationOptions(
                (storageLocationsRes.data.data || []).map((r) => ({
                    value: r.storage_code,
                    label: `${r.storage_code} - ${r.storage_location}`
                }))
            );

        } catch (err) {
            console.error(err);
        }
    };

    // Same API Product.js uses. The backend narrows every list by whatever is
    // already selected, and returns plain string arrays.
    const fetchFilterOptions = async () => {
        try {
            const res = await axios.get(
                `${MASTER_API}/product-master/filter-options`,
                {
                    headers: authHeaders(),
                    params: {
                        productType: filters.productType?.value || "",
                        category: filters.category?.value || "",
                        cableType: filters.cableType?.value || "",
                        code: filters.code?.value || "",
                        color: filters.color?.value || "",
                        noOfCores: filters.noOfCores?.value || "",
                        nominalCrossSection: filters.nominalCrossSection?.value || "",
                        referenceOfStandard: filters.referenceOfStandard?.value || "",
                        voltage: filters.voltage?.value || "",
                        subCategory: filters.subCategory?.value || ""
                    }
                }
            );
            setFilterOptions((prev) => ({ ...prev, ...res.data }));
        } catch (err) {
            console.error("Failed to fetch filter options", err.response?.data || err.message);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filters.productType,
        filters.category,
        filters.cableType,
        filters.code,
        filters.color,
        filters.noOfCores,
        filters.nominalCrossSection,
        filters.referenceOfStandard,
        filters.voltage,
        filters.subCategory
    ]);

    useEffect(() => {
        axios.get(`${MASTER_API}/product-master/productCodeDescriptionList`, {
            headers: authHeaders(),
            params: { productType: filters.productType?.value || "" }
        })
            .then((res) => setCodeDescList(res.data || []))
            .catch((err) => console.error("Failed to load product code list", err.response?.data || err.message));
    }, [filters.productType]);

    const toStrOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

    // Product Code options: codes that actually have stock (label "CODE - Description"),
    // narrowed to the selected Product Type.
    const typeCodeSet = new Set(codeDescList.map((i) => i.productCode));
    const cascadingProductCodeOptions = allProductCodeOptions.filter(
        (o) => !filters.productType || typeCodeSet.has(o.value)
    );

    const fetchStockReport = async () => {
        try {

            const res = await axios.post(
                `${PURCHASE_API}/batch-list/list`,
                {
                    productType: filters.productType?.value || "",
                    productCode: filters.productCode?.value || "",
                    batchNumber: filters.batchNumber?.value || "",
                    storageCode: filters.storageLocation?.value || "",
                    construction: filters.construction?.value || "",
                    code: filters.code?.value || "",
                    cableType: filters.cableType?.value || "",
                    category: filters.category?.value || "",
                    subCategory: filters.subCategory?.value || "",
                    qualityCategory: filters.qualityCategory?.value || "",
                    noOfCores: filters.noOfCores?.value || "",
                    color: filters.color?.value || "",
                    nominalCrossSection: filters.nominalCrossSection?.value || "",
                    referenceOfStandard: filters.referenceOfStandard?.value || "",
                    voltage: filters.voltage?.value || "",
                    date: filters.date || ""
                }
            );

            setTableData(res.data.data || []);

        } catch (err) {
            console.error(err);
        }
    };

    // Changing an attribute filter clears the filters after it (same order and rules
    // as Product.js) so selections never contradict each other.
    const ALL_ATTRIBUTE_KEYS = [
        "cableType", "code", "color", "noOfCores", "nominalCrossSection",
        "referenceOfStandard", "voltage", "construction", "subCategory", "qualityCategory"
    ];

    const handleFilterChange = (key, opt) => {
        setFilters((prev) => {
            const updated = { ...prev, [key]: opt };
            const rawMode = (prev.productType?.value || "").trim().toLowerCase() === "raw material";
            const order = (rawMode ? RAW_MATERIAL_FILTERS : FINISHED_FILTERS).map((f) => f.key);

            if (key === "category") {
                ALL_ATTRIBUTE_KEYS.forEach((k) => { updated[k] = null; });
            } else {
                const idx = order.indexOf(key);
                if (idx !== -1) order.slice(idx + 1).forEach((k) => { updated[k] = null; });
            }
            return updated;
        });
    };

    // ── Changing Product Type resets every type-specific filter, same as
    // Product.js, because the set of visible attribute filters changes. ──
    const handleProductTypeChange = (opt) => {
        setFilters((prev) => ({
            ...prev,
            productType: opt,
            construction: null,
            code: null,
            cableType: null,
            category: null,
            subCategory: null,
            qualityCategory: null,
            noOfCores: null,
            color: null,
            nominalCrossSection: null,
            referenceOfStandard: null,
            voltage: null,
            productCode: null
        }));
    };

    // ── Which attribute filters to show for the selected Product Type ──
    const isRawMaterial =
        (filters.productType?.value || "").trim().toLowerCase() === "raw material";
    const attributeFilters = isRawMaterial ? RAW_MATERIAL_FILTERS : FINISHED_FILTERS;

    // Keeps the dd-mm-yyyy text box in sync whenever filters.date changes
    // from elsewhere (e.g. a "clear filters" action resetting it to "").
    useEffect(() => {
        setDateDisplayText(isoToDisplayDate(filters.date));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.date]);

    // The "Created Date" filter is displayed as dd-mm-yyyy but stored/sent to
    // the backend as yyyy-mm-dd. This converts the stored ISO value to the
    // dd-mm-yyyy string shown in the text box.
    const isoToDisplayDate = (iso) => {
        if (!iso) return "";
        const [y, m, d] = iso.split("-");
        if (!y || !m || !d) return "";
        return `${d}-${m}-${y}`;
    };

    // Handles typing into the dd-mm-yyyy date filter box: auto-inserts the
    // dashes as the user types, and only commits a value to filters.date
    // (as yyyy-mm-dd) once a complete, valid date has been entered.
    const handleDateDisplayChange = (e) => {
        let raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 8); // ddmmyyyy digits only
        let formatted = raw;
        if (raw.length > 4) {
            formatted = `${raw.slice(0, 2)}-${raw.slice(2, 4)}-${raw.slice(4)}`;
        } else if (raw.length > 2) {
            formatted = `${raw.slice(0, 2)}-${raw.slice(2)}`;
        }
        setDateDisplayText(formatted);

        if (raw.length === 8) {
            const day = raw.slice(0, 2);
            const month = raw.slice(2, 4);
            const year = raw.slice(4, 8);
            const iso = `${year}-${month}-${day}`;
            const d = new Date(iso);
            if (!Number.isNaN(d.getTime()) && d.getFullYear() === Number(year)) {
                setFilters((prev) => ({ ...prev, date: iso }));
            }
        } else if (raw.length === 0) {
            setFilters((prev) => ({ ...prev, date: "" }));
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    // ── Display-only merge: the backend deliberately keeps one row per stock
    // row (s.id) so partial-inspection history isn't lost (see batchList.service.js).
    // On screen, though, rows that share Product Code + Batch Number + Storage
    // Location + Created Date (and the same Status/Quality Status, so distinct
    // states never get blended together) should read as a single line with the
    // qty summed, rather than one line per underlying stock row. ──
    const mergeByLocation = (rows) => {
        const map = new Map();

        rows.forEach((row) => {
            const key = [
                row.product_code,
                row.batch_number,
                row.storage_code,
                row.status,
                row.quality_status,
                formatDate(row.created_at)
            ].join("|");

            if (!map.has(key)) {
                map.set(key, { ...row, qty: 0, _mergedIds: [] });
            }

            const merged = map.get(key);
            merged.qty += Number(row.qty) || 0;
            merged._mergedIds.push(row.id);
        });

        return Array.from(map.values());
    };

    // ── Inspected tab: every row still Pending, plus every row that has been Inspected ──
    const inspectedRows = mergeByLocation(
        tableData.filter(
            (row) => row.quality_status === "Pending" || row.quality_status === "Inspected"
        )
    );

    // ── Rejected tab: every row rejected at any test stage ──
    const rejectedRows = mergeByLocation(
        tableData.filter(
            (row) => (row.quality_status || "").startsWith("Rejected")
        )
    );

    // ── Product tab: one line per product code, qty merged/summed across every
    // underlying stock row for that product — Pending + Inspected + Rejected —
    // so a batch split across multiple stock rows (e.g. partially rejected)
    // still shows its true total quantity here instead of just one split's
    // partial amount. Only product code, description, uom and qty are shown. ──
    const productRows = Object.values(
        tableData.reduce((acc, row) => {
            const key = row.product_code;
            if (!acc[key]) {
                acc[key] = {
                    product_code: row.product_code,
                    description: row.description,
                    uom: row.uom,
                    qty: 0
                };
            }
            acc[key].qty += Number(row.qty) || 0;
            return acc;
        }, {})
    );

    const activeData =
        activeTab === "inspected" ? inspectedRows :
        activeTab === "rejected" ? rejectedRows :
        productRows;

    // ── Export the currently active tab's full filtered result set (not just the
    // current page) to a .xlsx file. Column set mirrors whichever table is on
    // screen, since Product/Summary shows fewer columns than Inspected/Rejected. ──
    const handleExportExcel = () => {
        if (activeData.length === 0) return;

        const rows = activeData.map((row) => (
            activeTab === "product"
                ? {
                    "Product Code": row.product_code,
                    "Description": row.description || "-",
                    "UOM": row.uom || "-",
                    "Qty": row.qty
                }
                : {
                    "Product Type": row.productType || "-",
                    "Product Code": row.product_code,
                    "Description": row.description || "-",
                    "Batch Number": row.batch_number,
                    "UOM": row.uom || "-",
                    "Qty": row.qty,
                    "Storage Location": row.storage_location,
                    "Storage Code": row.storage_code,
                    "Created Date": formatDate(row.created_at),
                    "Status": row.status,
                    "Quality Status": row.quality_status
                }
        ));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");

        const productTypeLabel = (filters.productType?.value || "AllTypes").replace(/\s+/g, "");
        const dateLabel = filters.date || "AllDates";
        XLSX.writeFile(workbook, `StockReport_${productTypeLabel}_${dateLabel}.xlsx`);
    };

    const totalPages = Math.max(1, Math.ceil(activeData.length / pageSize));
    const paginatedData = activeData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const goToPrevPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    return (
        <Layout>
            <div className="container-fluid p-4">

                <div style={{ ...styles.pageTitleBar, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                        <h2 style={styles.pageTitle}>
                            Stock Report
                        </h2>

                        <p style={styles.pageSubtitle}>
                            Manage and view batch inventory details
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            setManualExists(null);
                            setManualFile(null);
                            setShowManual(true);
                            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                .then((res) => res.json())
                                .then((data) => {
                                    const norm = (v) => String(v || "").trim().toLowerCase();
                                    const match = (data?.data || []).find((m) =>
                                        norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                        norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                        norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                    );
                                    if (match && match.drive_link) {
                                        setManualFile(match);
                                        setManualExists(true);
                                    } else {
                                        setManualExists(false);
                                    }
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
                        style={{
                            background: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            color: "#3b82f6",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                    >
                        <FileText size={18} />
                        User Manual
                    </button>
                </div>

                {/* --- User Manual Popup Modal --- */}
                {showManual && (
                    <div
                        onClick={() => setShowManual(false)}
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                            backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                            alignItems: "center", justifyContent: "center", zIndex: 1100,
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#ffffff",
                                borderRadius: "12px",
                                width: "90vw",
                                height: "97vh",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                            }}
                        >
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                            }}>
                                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                    User Manual
                                </h4>
                                <button
                                    onClick={() => setShowManual(false)}
                                    style={{
                                        border: "none", background: "#f1f5f9", borderRadius: "8px",
                                        width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                                        color: "#475569", lineHeight: 1,
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            {manualExists === null && (
                                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                                    Checking for user manual...
                                </div>
                            )}

                            {manualExists === false && (
                                <div style={{
                                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                                }}>
                                    <FileText size={40} color="#cbd5e1" />
                                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                        User Manual Not Available
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                        The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                    </div>
                                </div>
                            )}

                            {manualExists === true && manualFile && (() => {
                                const MANUAL_SCALE = 1.25;
                                return (
                                    <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                        <iframe
                                            src={manualFile.drive_link.replace("/view", "/preview")}
                                            title={manualFile.file_name || "User Manual"}
                                            allow="autoplay"
                                            style={{
                                                border: "none",
                                                width: `${100 / MANUAL_SCALE}%`,
                                                height: `${100 / MANUAL_SCALE}%`,
                                                transform: `scale(${MANUAL_SCALE})`,
                                                transformOrigin: "top left",
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                <div style={styles.filterCard}>

                    {/* Row 1: Product Type */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label style={styles.compactLabel}>Product Type</label>
                            <Select
                                styles={selectStyles}
                                placeholder="Select Product Type"
                                options={productTypes}
                                value={filters.productType}
                                onChange={handleProductTypeChange}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    </div>

                    {/* Row 2: type-specific attribute filters.
                        Raw Material  -> Category, Sub Category, Quality Category
                        Anything else -> Category, Cable Type, Code, Color, Cores,
                                         Cross Section, Std. Ref, Voltage, Construction */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 15 }}>
                        {attributeFilters.map(({ key, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={styles.compactLabel}>{label}</label>
                                <Select
                                    placeholder={label}
                                    isClearable
                                    options={toStrOptions(filterOptions[key])}
                                    value={filters[key]}
                                    onChange={(opt) => handleFilterChange(key, opt)}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={cableFilterSelectStyles}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Row 3: Product Code, Batch Number, Storage Location */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Product Code</label>
                            <Select
                                placeholder="Select..."
                                options={cascadingProductCodeOptions}
                                value={filters.productCode}
                                onChange={(val) => handleFilterChange("productCode", val)}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={cableFilterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Batch Number</label>
                            <Select
                                placeholder="Select..."
                                options={batchNumberOptions}
                                value={filters.batchNumber}
                                onChange={(val) => handleFilterChange("batchNumber", val)}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={cableFilterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Storage Location</label>
                            <Select
                                placeholder="Select..."
                                options={storageLocationOptions}
                                value={filters.storageLocation}
                                onChange={(val) => handleFilterChange("storageLocation", val)}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={cableFilterSelectStyles}
                            />
                        </div>

                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.compactLabel}>Created Date</label>
                            <input
                                type="text"
                                placeholder="dd-mm-yyyy"
                                value={dateDisplayText}
                                onChange={handleDateDisplayChange}
                                maxLength={10}
                                style={styles.dateInput}
                            />
                        </div>
                    </div>

                </div>

                <div style={{ ...styles.tabBarWrap, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={styles.tabBar}>
                        <button
                            style={activeTab === "inspected" ? styles.tabBtnActive : styles.tabBtn}
                            onClick={() => handleTabChange("inspected")}
                        >
                            Inspected
                            <span style={activeTab === "inspected" ? styles.tabBadge : styles.tabBadgeInactive}>
                                {inspectedRows.length}
                            </span>
                        </button>
                        <button
                            style={activeTab === "rejected" ? styles.tabBtnActive : styles.tabBtn}
                            onClick={() => handleTabChange("rejected")}
                        >
                            Rejected
                            <span style={activeTab === "rejected" ? styles.tabBadge : styles.tabBadgeInactive}>
                                {rejectedRows.length}
                            </span>
                        </button>
                        <button
                            style={activeTab === "product" ? styles.tabBtnActive : styles.tabBtn}
                            onClick={() => handleTabChange("product")}
                        >
                            Summary
                            <span style={activeTab === "product" ? styles.tabBadge : styles.tabBadgeInactive}>
                                {productRows.length}
                            </span>
                        </button>
                    </div>

                    <button
                        onClick={handleExportExcel}
                        disabled={activeData.length === 0}
                        style={activeData.length === 0 ? styles.exportBtnDisabled : styles.exportBtn}
                        title="Export the currently filtered rows to Excel"
                    >
                        Export to Excel
                    </button>
                </div>

                <div style={styles.tableContainer}>

                    <div className="table-responsive">

                        <table className="table" style={styles.erpTable}>

                            {activeTab === "product" ? (

                                <>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Description</th>
                                            <th style={styles.erpTh}>UOM</th>
                                            <th style={styles.erpTh}>Qty</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {paginatedData.length === 0 ? (

                                            <tr>
                                                <td colSpan="4" className="text-center">
                                                    No Records Found
                                                </td>
                                            </tr>

                                        ) : (

                                            paginatedData.map((row) => (

                                                <tr key={row.product_code}>
                                                    <td style={styles.erpTd}>
                                                        <Link
                                                            to={`/cable/product/product-code?view=${encodeURIComponent(
                                                                row.product_code
                                                            )}&returnTo=${encodeURIComponent(
                                                                location.pathname + location.search
                                                            )}`}
                                                            style={styles.cellLink}
                                                        >
                                                            {row.product_code}
                                                        </Link>
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.description || "-"}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.uom || "-"}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {formatQty(row.qty)}
                                                    </td>
                                                </tr>

                                            ))

                                        )}

                                    </tbody>
                                </>

                            ) : (

                                <>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Product Type</th>
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Description</th>
                                            <th style={styles.erpTh}>Batch Number</th>
                                            <th style={styles.erpTh}>UOM</th>
                                            <th style={styles.erpTh}>Qty</th>
                                            <th style={styles.erpTh}>Storage Location</th>
                                            <th style={styles.erpTh}>Storage Code</th>
                                            <th style={styles.erpTh}>Created Date</th>
                                            <th style={styles.erpTh}>Status</th>
                                            <th style={styles.erpTh}>Quality Status</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {paginatedData.length === 0 ? (

                                            <tr>
                                                <td
                                                    colSpan="11"
                                                    className="text-center"
                                                >
                                                    No Records Found
                                                </td>
                                            </tr>

                                        ) : (

                                            paginatedData.map((row) => (

                                                <tr key={row._mergedIds ? row._mergedIds.join("-") : row.id}>
                                                    <td style={styles.erpTd}>
                                                        {row.productType || "-"}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        <Link
                                                            to={`/cable/product/product-code?view=${encodeURIComponent(
                                                                row.product_code
                                                            )}&returnTo=${encodeURIComponent(
                                                                location.pathname + location.search
                                                            )}`}
                                                            style={styles.cellLink}
                                                        >
                                                            {row.product_code}
                                                        </Link>
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.description || "-"}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.batch_number}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.uom || "-"}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {formatQty(row.qty)}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.storage_location}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.storage_code}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {formatDate(row.created_at)}
                                                    </td>

                                                    <td style={styles.erpTd}>
                                                        {row.status}
                                                    </td>
                                                    <td style={styles.erpTd}>{row.quality_status}</td>
                                                </tr>

                                            ))

                                        )}

                                    </tbody>
                                </>

                            )}

                        </table>

                    </div>

                </div>

                <div style={styles.paginationBar}>
                    <div style={styles.paginationInfo}>
                        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> • Total {activeData.length}
                    </div>

                    <div style={styles.paginationButtons}>
                        <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 1}
                            style={currentPage === 1 ? styles.paginationBtnDisabled : styles.paginationBtn}
                        >
                            Prev
                        </button>

                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages}
                            style={currentPage === totalPages ? styles.paginationBtnDisabled : styles.paginationBtn}
                        >
                            Next
                        </button>

                        <select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            style={styles.pageSizeSelect}
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size} / page
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

            </div>
        </Layout>
    );
};

export default BatchList;